import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';
import { Clipboard } from '@capacitor/clipboard';

const isNative = () => Capacitor.isNativePlatform();

/**
 * Download a file from a URL.
 * On native: fetches, writes to cache, opens share sheet.
 * On web: opens in new tab via <a> click.
 */
export const downloadUrl = async (url, fileName = 'document') => {
  if (!isNative()) {
    const link = window.document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    return;
  }

  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch file: ${response.status}`);
  }
  const arrayBuffer = await response.arrayBuffer();
  const base64 = btoa(
    new Uint8Array(arrayBuffer).reduce((data, byte) => data + String.fromCharCode(byte), '')
  );

  await Filesystem.writeFile({
    path: fileName,
    data: base64,
    directory: Directory.Cache,
  });

  const { uri } = await Filesystem.getUri({
    path: fileName,
    directory: Directory.Cache,
  });

  await Share.share({
    title: fileName,
    url: uri,
    dialogTitle: 'Open or save document',
  });
};

/**
 * Download a file from base64 content.
 * On native: writes to cache, opens share sheet.
 * On web: creates a Blob URL and triggers <a> click.
 */
export const downloadBase64 = async (base64Data, fileName = 'document', mimeType = 'application/octet-stream') => {
  if (!isNative()) {
    const byteChars = atob(base64Data);
    const byteArray = new Uint8Array(byteChars.length);
    for (let i = 0; i < byteChars.length; i++) {
      byteArray[i] = byteChars.charCodeAt(i);
    }
    const blob = new Blob([byteArray], { type: mimeType });
    const blobUrl = URL.createObjectURL(blob);
    const link = window.document.createElement('a');
    link.href = blobUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(blobUrl);
    return;
  }

  await Filesystem.writeFile({
    path: fileName,
    data: base64Data,
    directory: Directory.Cache,
  });

  const { uri } = await Filesystem.getUri({
    path: fileName,
    directory: Directory.Cache,
  });

  await Share.share({
    title: fileName,
    url: uri,
    dialogTitle: 'Open or save document',
  });
};

/**
 * Copy text to clipboard.
 * On native: uses @capacitor/clipboard (works without HTTPS requirement).
 * On web: falls back to navigator.clipboard.
 */
export const copyToClipboard = async (text) => {
  if (isNative()) {
    await Clipboard.write({ string: text });
    return;
  }
  await navigator.clipboard.writeText(text);
};
